import { useRef, useState, type Dispatch, type SetStateAction } from "react";
import type {
  CommandId,
  EnvironmentId,
  ScopedThreadRef,
  ModelSelection,
  RuntimeMode,
  ProviderInteractionMode,
} from "@cmmd-forge/contracts";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";

import {
  useComposerDraftStore,
  type PersistedComposerImageAttachment,
} from "../../composerDraftStore";
import { prepareThreadExecutionIntentAction } from "../../lib/runThreadExecutionIntentAction";
import { readThreadCmmdProjectId } from "../../lib/threadCmmdProjectId";
import { newCommandId, newMessageId } from "../../lib/utils";
import { resolveComposerSubmissionIdentity } from "./composerSubmissionIdentity";
import { persistRemoteComposerImages } from "./persistRemoteComposerImages";

export interface RemoteThreadDispatchSettings {
  readonly modelSelection: ModelSelection;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
}

/**
 * CMMD's typed refusal code for a send that arrives while the Rollout has an
 * update in flight on this Environment. It is retryable and the client holds
 * the message, so it is a wait, never a failed send.
 */
const UPDATING_REFUSAL_CODE = "runtime_updating";

export type RemoteThreadExecutionIntentState =
  | { readonly kind: "idle" }
  | { readonly kind: "connecting" }
  /** The Environment is updating. The staged message is held until it finishes. */
  | { readonly kind: "updating" }
  | { readonly kind: "ready" }
  | { readonly kind: "failed"; readonly message: string };

/**
 * Why a staged send is waiting rather than failing, in the typed form the
 * Environment Notice renders. Only the states that hold the message have one.
 */
export type RemoteThreadEnvironmentWaitingReason = "updating";

/**
 * The waiting reason to show under the retained thread view, or null when the
 * intent is not waiting on the Environment.
 */
export function remoteThreadEnvironmentWaitingReason(
  state: RemoteThreadExecutionIntentState,
): RemoteThreadEnvironmentWaitingReason | null {
  return state.kind === "updating" ? "updating" : null;
}

export interface RemoteThreadExecutionIntentController {
  readonly state: RemoteThreadExecutionIntentState;
  readonly request: (settings?: RemoteThreadDispatchSettings) => void;
}

interface RemoteThreadExecutionIntentSnapshot {
  readonly targetKey: string;
  readonly state: RemoteThreadExecutionIntentState;
}

interface RemoteThreadExecutionIntentOperation {
  readonly targetKey: string;
  readonly promise: Promise<unknown>;
}

type ComposerDraftStore = ReturnType<typeof useComposerDraftStore.getState>;

function executionIntentFailureMessage(error: unknown): string {
  return error instanceof Error && error.message.trim().length > 0
    ? error.message
    : "Forge could not start this Environment.";
}

function replayRemoteThreadCommandId(input: {
  readonly store: ComposerDraftStore;
  readonly threadRef: ScopedThreadRef;
  readonly runtimeId: string;
  readonly settings?: RemoteThreadDispatchSettings;
}): CommandId | null {
  const draft = input.store.getComposerDraft(input.threadRef);
  if (!draft) return null;
  const staged = draft.stagedSubmission;
  if (!staged) return null;
  const current = {
    prompt: draft.prompt,
    attachmentIds: draft.persistedAttachments.map((attachment) => attachment.id),
    fileIds: (draft.files ?? []).map((attachment) => attachment.id),
    terminalContextIds: draft.terminalContexts.map((context) => context.id),
  };
  const identity = resolveComposerSubmissionIdentity({
    priorSubmission: staged,
    current: {
      ...current,
      ...input.settings,
    },
    now: () => new Date().toISOString(),
    createCommandId: newCommandId,
    createMessageId: newMessageId,
  });
  return identity.replaysPriorSubmission && identity.replayRuntimeId === input.runtimeId
    ? identity.commandId
    : null;
}

function stageRemoteThreadExecutionIntent(input: {
  readonly store: ComposerDraftStore;
  readonly threadRef: ScopedThreadRef;
  readonly runtimeId: string;
  readonly settings?: RemoteThreadDispatchSettings;
  readonly attachments?: ReadonlyArray<PersistedComposerImageAttachment>;
}): CommandId {
  const replayCommandId = replayRemoteThreadCommandId(input);
  const commandId = replayCommandId ?? newCommandId();
  if (!replayCommandId) {
    if (input.settings) {
      input.store.setModelSelection(input.threadRef, input.settings.modelSelection);
      input.store.setRuntimeMode(input.threadRef, input.settings.runtimeMode);
      input.store.setInteractionMode(input.threadRef, input.settings.interactionMode);
    }
    input.store.stageComposerSubmission(
      input.threadRef,
      new Date().toISOString(),
      commandId,
      newMessageId(),
      { runtimeId: input.runtimeId },
      input.settings,
      input.attachments,
    );
  }
  input.store.setPendingAutoSubmit(input.threadRef, true);
  return commandId;
}

function completeRemoteThreadExecutionIntent(input: {
  readonly prepared: Awaited<ReturnType<typeof prepareThreadExecutionIntentAction>>;
  readonly operation: Promise<unknown>;
  readonly operationRef: {
    current: RemoteThreadExecutionIntentOperation | null;
  };
  readonly store: ComposerDraftStore;
  readonly threadRef: ScopedThreadRef;
  readonly targetKey: string;
  readonly setSnapshot: Dispatch<SetStateAction<RemoteThreadExecutionIntentSnapshot>>;
}): void {
  if (input.operationRef.current?.promise !== input.operation) return;
  input.operationRef.current = null;
  if (input.prepared.intent?.status === "dispatched") {
    input.store.clearComposerContent(input.threadRef);
    input.store.setPendingAutoSubmit(input.threadRef, false);
  } else if (input.prepared.intent) {
    input.store.setStagedComposerExecutionIntent(input.threadRef, {
      runtimeId: input.prepared.intent.runtimeId,
      intentId: input.prepared.intent.id,
    });
  }
  input.setSnapshot({ targetKey: input.targetKey, state: { kind: "ready" } });
}

function failRemoteThreadExecutionIntent(input: {
  readonly error: unknown;
  readonly operation: Promise<unknown>;
  readonly operationRef: {
    current: RemoteThreadExecutionIntentOperation | null;
  };
  readonly store: ComposerDraftStore;
  readonly threadRef: ScopedThreadRef;
  readonly targetKey: string;
  readonly setSnapshot: Dispatch<SetStateAction<RemoteThreadExecutionIntentSnapshot>>;
}): void {
  if (input.operationRef.current?.promise !== input.operation) return;
  input.store.setPendingAutoSubmit(input.threadRef, false);
  input.operationRef.current = null;
  input.setSnapshot({
    targetKey: input.targetKey,
    state: {
      kind: "failed",
      message: executionIntentFailureMessage(input.error),
    },
  });
}

/**
 * Stages a remote Draft for the normal live composer, then enters the shared
 * Environment execution gate. The live chat owns dispatch after connection so
 * this bootstrap path cannot grow a second submission implementation.
 */
export function useRemoteThreadExecutionIntent(input: {
  readonly environmentId: EnvironmentId;
  readonly runtimeId: string;
  readonly threadRef: ScopedThreadRef;
}): RemoteThreadExecutionIntentController {
  const targetKey = scopedThreadKey(input.threadRef);
  const [snapshot, setSnapshot] = useState<RemoteThreadExecutionIntentSnapshot>({
    targetKey,
    state: { kind: "idle" },
  });
  const operationRef = useRef<RemoteThreadExecutionIntentOperation | null>(null);
  const state = snapshot.targetKey === targetKey ? snapshot.state : { kind: "idle" as const };

  const request = (settings?: RemoteThreadDispatchSettings) => {
    if (operationRef.current?.targetKey === targetKey) return;
    const store = useComposerDraftStore.getState();
    const prepare = (attachments?: ReadonlyArray<PersistedComposerImageAttachment>) => {
      const commandId = stageRemoteThreadExecutionIntent({
        store,
        threadRef: input.threadRef,
        runtimeId: input.runtimeId,
        ...(settings ? { settings } : {}),
        ...(attachments ? { attachments } : {}),
      });
      return prepareThreadExecutionIntentAction(input.environmentId, {
        runtimeId: input.runtimeId,
        action: "send",
        projectId: readThreadCmmdProjectId(input.threadRef),
        targetId: input.threadRef.threadId,
        dispatchIdentity: commandId,
        // A retry can report a wait after the person has moved to another
        // thread. Only the operation this hook still owns may write; the
        // same guard completion and failure use through operationRef.
        onWaiting: (_message, code) => {
          if (operationRef.current?.promise !== operation) return;
          setSnapshot({
            targetKey,
            state: { kind: code === UPDATING_REFUSAL_CODE ? "updating" : "connecting" },
          });
        },
      });
    };
    let operation: ReturnType<typeof prepareThreadExecutionIntentAction>;
    try {
      setSnapshot({ targetKey, state: { kind: "connecting" } });
      operation = store.getComposerDraft(input.threadRef)?.images.length
        ? persistRemoteComposerImages(input.threadRef).then(prepare)
        : prepare();
    } catch (error) {
      store.setPendingAutoSubmit(input.threadRef, false);
      setSnapshot({
        targetKey,
        state: {
          kind: "failed",
          message: executionIntentFailureMessage(error),
        },
      });
      return;
    }
    operationRef.current = { targetKey, promise: operation };
    void operation.then(
      (prepared) =>
        completeRemoteThreadExecutionIntent({
          prepared,
          operation,
          operationRef,
          store,
          threadRef: input.threadRef,
          targetKey,
          setSnapshot,
        }),
      (error: unknown) =>
        failRemoteThreadExecutionIntent({
          error,
          operation,
          operationRef,
          store,
          threadRef: input.threadRef,
          targetKey,
          setSnapshot,
        }),
    );
  };

  return { state, request };
}
